/**
 * FeedbackButton — pływający przycisk do zgłaszania błędów, pomysłów i pytań.
 * Zaadaptowany z school-transport-platform.
 * Zmiany: next/link → react-router, react-hot-toast → sonner, UI po polsku.
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquarePlus, X, Bug, Lightbulb, HelpCircle, Loader2,
  CheckCircle2, AlertCircle, Send, Zap, AlertTriangle, Minus, MapPin, ListChecks, ArrowRight,
  SlidersHorizontal, Paperclip, ThumbsUp, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/shared/lib/api';
import { useAuthStore } from '@store/auth.store';
import { EntityLinkPicker, type LinkedEntity } from './EntityLinkPicker';

interface SimilarTicket { id: string; ticketNumber: string; title: string; type: string; voteCount: number; hasVoted: boolean }

type FeedbackType = 'BUG' | 'IDEA' | 'MODYFIKACJA' | 'QUESTION' | 'OTHER';
type Urgency = 'BLOCKING' | 'HINDERING' | 'MINOR';

const TYPES: { value: FeedbackType; label: string; icon: React.ElementType; color: string; bg: string; desc: string }[] = [
  { value: 'BUG',         label: 'Błąd',        icon: Bug,               color: '#dc2626', bg: '#fee2e2', desc: 'Coś nie działa lub zachowuje się niepoprawnie.' },
  { value: 'IDEA',        label: 'Pomysł',      icon: Lightbulb,         color: '#d97706', bg: '#fef3c7', desc: 'Zaproponuj zupełnie nową funkcję lub ulepszenie.' },
  { value: 'MODYFIKACJA', label: 'Modyfikacja', icon: SlidersHorizontal, color: '#7c3aed', bg: '#ede9fe', desc: 'Zmiana w istniejącej, działającej funkcji — dostosowanie zachowania, układu lub treści (to nie błąd ani nowy pomysł).' },
  { value: 'QUESTION',    label: 'Pytanie',     icon: HelpCircle,        color: '#0891b2', bg: '#cffafe', desc: 'Coś jest niejasne lub wymaga wyjaśnienia.' },
  { value: 'OTHER',       label: 'Inne',        icon: MessageSquarePlus, color: '#64748b', bg: '#f1f5f9', desc: 'Cokolwiek innego.' },
];

const MAX_FILES = 6;
const MAX_FILE_MB = 50;

const URGENCY: { value: Urgency; label: string; hint: string; icon: React.ElementType; color: string; bg: string }[] = [
  { value: 'BLOCKING',  label: 'Blokuje',  hint: 'Nie mogę pracować', icon: Zap,           color: '#dc2626', bg: '#fee2e2' },
  { value: 'HINDERING', label: 'Utrudnia', hint: 'Da się obejść',      icon: AlertTriangle, color: '#d97706', bg: '#fef3c7' },
  { value: 'MINOR',     label: 'Drobiazg', hint: 'Kosmetyka / pomysł', icon: Minus,         color: '#0891b2', bg: '#cffafe' },
];

/** Friendly PL name for the screen the report was filed from (folded into context for triage). */
const SCREEN_NAMES: { prefix: string; label: string }[] = [
  { prefix: '/execution', label: 'Wykonanie instruktorów' },
  { prefix: '/road-cards', label: 'Karty drogowe' },
  { prefix: '/rozklad', label: 'Rozkład jazdy' },
  { prefix: '/realizacja-wozow', label: 'Realizacja planu wozów' },
  { prefix: '/worktime', label: 'Rozliczenie czasu pracy (RCP)' },
  { prefix: '/planner', label: 'Planer' },
  { prefix: '/admin/tasks', label: 'Dyspozycja' },
  { prefix: '/mapa-pojazdow', label: 'Mapa pojazdów' },
  { prefix: '/task-templates', label: 'Definicje czynności' },
  { prefix: '/forms/kontrole-trzezwosci', label: 'Kontrole trzeźwości' },
  { prefix: '/forms/kontrole-predkosci', label: 'Kontrole prędkości' },   // B-675 — przed `/forms`   // B-655 — przed `/forms` (pierwsze trafienie wygrywa)
  { prefix: '/forms', label: 'Formularze' },
  { prefix: '/duty-log', label: 'Dziennik służby' },
  { prefix: '/devices', label: 'Urządzenia' },
  { prefix: '/vehicles', label: 'Pojazdy' },
  { prefix: '/users', label: 'Użytkownicy' },
  { prefix: '/messages', label: 'Wiadomości' },
  { prefix: '/monitoring', label: 'Monitoring' },
  { prefix: '/feedback/mine', label: 'Moje zgłoszenia' },
  { prefix: '/feedback', label: 'Centrum Zgłoszeń' },
  { prefix: '/notifications', label: 'Powiadomienia' },
  { prefix: '/dashboard', label: 'Pulpit' },
];
function screenName(pathname: string): string {
  const hit = SCREEN_NAMES.find(s => pathname === s.prefix || pathname.startsWith(s.prefix + '/'));
  if (hit) return hit.label;
  if (pathname === '/' || pathname === '') return 'Pulpit';
  const slug = pathname.split('/').filter(Boolean)[0] ?? '';
  return slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : 'Panel';
}

const ACCENT = '#0d9488';
const PRODUCT_CODE = 'zur-ops';

function shade(hex: string, percent: number): string {
  const h = hex.replace('#', '');
  const num = parseInt(h, 16);
  const r = Math.max(0, Math.min(255, (num >> 16) + Math.round((255 * percent) / 100)));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + Math.round((255 * percent) / 100)));
  const b = Math.max(0, Math.min(255, (num & 0xff) + Math.round((255 * percent) / 100)));
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}

export function FeedbackButton() {
  const user = useAuthStore(s => s.user);
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>('BUG');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [urgency, setUrgency] = useState<Urgency | null>(null);
  const [steps, setSteps] = useState('');
  const [linked, setLinked] = useState<LinkedEntity | null>(null);
  const [currentState, setCurrentState] = useState('');
  const [expectedChange, setExpectedChange] = useState('');
  const [rationale, setRationale] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [similar, setSimilar] = useState<SimilarTicket[]>([]);
  const [votedIds, setVotedIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<{ ticketNumber: string } | null>(null);
  const [hovered, setHovered] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);

  /**
   * B-201 (ZUR-2026-0056): kliknięcie w tło zamykało okno, a efekt niżej po 250 ms
   * czyścił WSZYSTKIE pola — godzina zbierania myśli znikała przez jedno chybione
   * kliknięcie. Przy niepustym formularzu tło przestaje zamykać; zostaje krzyżyk,
   * czyli świadoma decyzja o porzuceniu treści.
   */
  const maNiezapisanaTresc =
    !!(title.trim() || description.trim() || steps.trim() ||
       currentState.trim() || expectedChange.trim() || rationale.trim() || files.length);

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setTitle(''); setDescription(''); setType('BUG'); setUrgency(null); setSteps('');
        setLinked(null); setCurrentState(''); setExpectedChange(''); setRationale(''); setFiles([]);
        setSimilar([]); setVotedIds(new Set()); setSubmitted(null);
      }, 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Auto-hide the chip whenever any full-screen overlay (modal/drawer) is open.
  // Every modal/drawer in the app renders a `.fixed.inset-0` backdrop; a
  // MutationObserver watches for one and fades the chip out so it never
  // collides with a modal's/drawer's action buttons. (reusable-conventions §5)
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const check = () => {
      const overlays = Array.from(document.querySelectorAll<HTMLElement>('.fixed.inset-0'));
      setOverlayOpen(overlays.some((el) => {
        const cs = getComputedStyle(el);
        return cs.display !== 'none' && cs.visibility !== 'hidden';
      }));
    };
    check();
    let raf = 0;
    const schedule = () => { if (raf) return; raf = requestAnimationFrame(() => { raf = 0; check(); }); };
    const obs = new MutationObserver(schedule);
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });
    return () => { obs.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, []);

  const screen = useMemo(() => screenName(location.pathname), [location.pathname]);
  const baseContext = useMemo(() => ({
    screen,
    url:       typeof window !== 'undefined' ? window.location.href : null,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    viewport:  typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : null,
    userRole:  user?.role,
  }), [user?.role, screen]);

  // Suggest existing OPEN tickets as the reporter types a title — so they can
  // "Ja też" (upvote) instead of filing a duplicate.
  const searchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    if (submitted) return;
    clearTimeout(searchTimer.current);
    const q = title.trim();
    if (q.length < 3) { setSimilar([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const res: any = await api.get(`/feedback/search?q=${encodeURIComponent(q)}&productCode=${PRODUCT_CODE}`);
        setSimilar(Array.isArray(res) ? res : (res?.data ?? []));
      } catch { setSimilar([]); }
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [title, submitted]);

  if (!user) return null;

  async function voteFor(t: SimilarTicket) {
    try {
      const res: any = await api.post(`/feedback/${t.id}/vote`, {});
      const r = res?.data ?? res;
      setVotedIds(prev => { const n = new Set(prev); if (r.hasVoted) n.add(t.id); else n.delete(t.id); return n; });
      setSimilar(prev => prev.map(x => x.id === t.id ? { ...x, voteCount: r.voteCount, hasVoted: r.hasVoted } : x));
      if (r.hasVoted) toast.success(`Dołączono do ${t.ticketNumber} — dziękujemy!`);
    } catch { toast.error('Nie udało się zagłosować'); }
  }

  function addFiles(picked: FileList | File[] | null) {
    if (!picked) return;
    const incoming = Array.from(picked as ArrayLike<File>);
    const tooBig = incoming.find(f => f.size > MAX_FILE_MB * 1024 * 1024);
    if (tooBig) { toast.error(`Plik „${tooBig.name}" przekracza ${MAX_FILE_MB} MB.`); }
    const ok = incoming.filter(f => f.size <= MAX_FILE_MB * 1024 * 1024);
    setFiles(prev => [...prev, ...ok].slice(0, MAX_FILES));
  }
  /**
   * B-191 (zgłoszenie ZUR-2026-0045) — wklejanie zrzutów ekranu ze schowka.
   *
   * Zgłaszający robi wycinek narzędziem systemowym i ma go w schowku, a nie na
   * dysku. Bez tego musiałby najpierw zapisać plik, żeby zaraz go wybrać —
   * najczęstsza droga zgłaszania błędu prowadziła przez najbardziej okrężną.
   *
   * Nazwa nadawana tutaj, bo zrzut ze schowka przychodzi zwykle jako „image.png"
   * albo w ogóle bez nazwy; przy kilku wklejeniach lista byłaby nieczytelna.
   * Znacznik czasu jest lokalny i ma tylko rozróżniać wklejenia, nie datować ich.
   */
  function pasteFromClipboard(e: React.ClipboardEvent) {
    const items = Array.from(e.clipboardData?.items ?? []);
    const images = items.filter((it) => it.kind === 'file' && it.type.startsWith('image/'));
    if (images.length === 0) return;   // zwykły tekst — nie przechwytujemy wklejania

    const picked: File[] = [];
    for (const it of images) {
      const blob = it.getAsFile();
      if (!blob) continue;
      const ext = (blob.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
      const t = new Date();
      const hhmmss = [t.getHours(), t.getMinutes(), t.getSeconds()]
        .map((n) => String(n).padStart(2, '0')).join('');
      picked.push(new File([blob], `zrzut-${hhmmss}-${picked.length + 1}.${ext}`, { type: blob.type }));
    }
    if (picked.length === 0) return;

    // Dopiero teraz blokujemy domyślne wklejenie — inaczej zjadałoby wklejanie
    // tekstu do pól opisu, co jest tu czynnością znacznie częstszą.
    e.preventDefault();
    const wolne = MAX_FILES - files.length;
    if (wolne <= 0) { toast.error(`Maksymalnie ${MAX_FILES} załączników.`); return; }
    addFiles(picked.slice(0, wolne));
    toast.success(picked.length === 1 ? 'Zrzut dołączony do zgłoszenia' : `Dołączono ${Math.min(picked.length, wolne)} zrzuty`);
  }

  function removeFile(idx: number) {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  }
  function fileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  async function submit() {
    if (title.trim().length < 3 || description.trim().length < 3) {
      toast.error('Tytuł i opis muszą mieć minimum 3 znaki.');
      return;
    }
    setSubmitting(true);
    try {
      const res: any = await api.post('/feedback', {
        productCode: PRODUCT_CODE,
        type, title: title.trim(), description: description.trim(),
        context: {
          ...baseContext,
          ...(urgency ? { reporterUrgency: urgency } : {}),
          ...(type === 'BUG' && steps.trim() ? { stepsToReproduce: steps.trim() } : {}),
          ...(linked ? { linkedEntity: linked } : {}),
          ...(type === 'MODYFIKACJA' && currentState.trim() ? { currentState: currentState.trim() } : {}),
          ...(type === 'MODYFIKACJA' && expectedChange.trim() ? { expectedChange: expectedChange.trim() } : {}),
          ...(type === 'MODYFIKACJA' && rationale.trim() ? { rationale: rationale.trim() } : {}),
          submittedAt: new Date().toISOString(),
        },
      });
      const ticket = res?.data ?? res;
      // Upload any picked attachments against the freshly-created ticket. A failed
      // file doesn't fail the whole report — the ticket is already saved.
      let failed = 0;
      for (const file of files) {
        try {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('feedbackTicketId', ticket.id);
          await api.upload('/attachments/upload', fd);
        } catch { failed++; }
      }
      setSubmitted({ ticketNumber: ticket.ticketNumber });
      if (failed > 0) toast.error(`Zgłoszenie zapisane, ale ${failed} z ${files.length} plików nie udało się dołączyć.`);
      toast.success(`Dziękujemy! Numer zgłoszenia: ${ticket.ticketNumber}.`, { duration: 5000 });
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? 'Nie udało się wysłać zgłoszenia. Spróbuj ponownie.';
      toast.error(Array.isArray(msg) ? msg.join(', ') : String(msg));
    } finally {
      setSubmitting(false);
    }
  }

  const cfg = TYPES.find(t => t.value === type)!;

  // Hide the FAB whenever it would collide with a persistent bottom-anchored
  // composer. The Messages view keeps its send box in the very bottom-right
  // corner — exactly where this chip sits — so we tuck the chip away there
  // (the same fade-out treatment used for full-screen overlays). Feedback
  // stays reachable from the sidebar "Zgłoszenia" entry.
  const onComposerPage = location.pathname.startsWith('/messages');
  const fabHidden = overlayOpen || onComposerPage;

  return (
    <>
      {/* Compact icon FAB — a ~48px circle that expands the label on hover.
          Auto-hides (opacity/pointer-events) whenever a full-screen overlay
          is open, so it never collides with modal/drawer buttons. (§5) */}
      <button
        onClick={() => setOpen(true)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        title="Zgłoś błąd, zaproponuj pomysł lub zadaj pytanie"
        aria-label="Zgłoś uwagę"
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          height: 48, padding: '0 14px',
          background: `linear-gradient(135deg, ${ACCENT}, ${shade(ACCENT, -15)})`,
          color: 'white', border: 'none', borderRadius: 999,
          fontSize: 13, fontWeight: 700, cursor: 'pointer',
          boxShadow: hovered ? `0 12px 32px ${ACCENT}77` : `0 8px 24px ${ACCENT}55`,
          opacity: fabHidden ? 0 : 1,
          pointerEvents: fabHidden ? 'none' : 'auto',
          transform: fabHidden ? 'translateY(10px) scale(.9)' : (hovered ? 'translateY(-2px)' : 'none'),
          transition: 'opacity .2s ease, transform .2s ease, box-shadow .18s ease',
        }}
      >
        <MessageSquarePlus className="w-5 h-5" style={{ flexShrink: 0 }} />
        <span
          style={{
            display: 'inline-block', whiteSpace: 'nowrap', overflow: 'hidden',
            maxWidth: hovered ? 110 : 0,
            marginLeft: hovered ? 8 : 0,
            opacity: hovered ? 1 : 0,
            transition: 'max-width .22s ease, margin-left .22s ease, opacity .22s ease',
          }}
        >
          Zgłoś uwagę
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            // B-191: nasłuch na całym oknie, nie na pojedynczym polu — zgłaszający
            // wkleja zrzut w dowolnym momencie, nie celując w konkretny input.
            onPaste={pasteFromClipboard}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(3px)' }}
            onClick={() => {
              if (submitting) return;
              if (maNiezapisanaTresc) {
                // Bez komunikatu kliknięcie w tło nie robi NIC i wygląda na zawieszone
                // okno — użytkownik musi wiedzieć, dlaczego nie zniknęło.
                toast.warning('Masz niezapisaną treść — zamknij krzyżykiem, jeśli chcesz ją porzucić.');
                return;
              }
              setOpen(false);
            }}
          >
            <motion.div
              initial={{ scale: 0.96, y: 12, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.96, y: 12, opacity: 0 }}
              transition={{ type: 'spring', damping: 24, stiffness: 280 }}
              className="w-full max-w-lg bg-card rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-border"
              style={{ maxHeight: '85vh' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Nagłówek */}
              <div className="relative overflow-hidden flex-shrink-0"
                style={{ background: `linear-gradient(135deg, ${ACCENT}, ${shade(ACCENT, -20)})`, color: 'white' }}>
                <div className="absolute -top-12 -right-8 w-40 h-40 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }} />
                <div className="relative flex items-center justify-between px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: 'rgba(255,255,255,0.18)' }}>
                      <MessageSquarePlus className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="text-lg font-extrabold leading-tight">
                        {submitted ? 'Zgłoszono!' : 'Wyślij uwagę'}
                      </h2>
                      <p className="text-[11px] opacity-80 mt-0.5">
                        {submitted ? 'Śledzimy postęp Twojego zgłoszenia.' : 'Większość pól jest wypełniana automatycznie.'}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => !submitting && setOpen(false)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: 'rgba(255,255,255,0.18)' }}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Treść */}
              {submitted ? (
                <div className="flex-1 px-6 py-8 flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                    style={{ background: `${ACCENT}22`, color: ACCENT }}>
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h3 className="text-base font-bold text-foreground">Zgłoszenie przyjęte</h3>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">Numer zgłoszenia:</p>
                  <div className="mt-3 px-4 py-2 rounded-xl font-mono text-sm font-bold"
                    style={{ background: `${ACCENT}11`, color: ACCENT, border: `1px solid ${ACCENT}33` }}>
                    {submitted.ticketNumber}
                  </div>
                  <p className="text-[12px] text-muted-foreground mt-4 max-w-sm">
                    Znajdziesz to zgłoszenie w sekcji <strong>Moje zgłoszenia</strong>. Poinformujemy Cię o każdej zmianie statusu.
                  </p>
                  <div className="mt-6 flex items-center gap-2">
                    <Link to="/feedback/mine" onClick={() => setOpen(false)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all hover:brightness-110"
                      style={{ background: ACCENT }}>
                      Zobacz moje zgłoszenia <ArrowRight className="w-4 h-4" />
                    </Link>
                    <button onClick={() => setOpen(false)}
                      className="px-4 py-2 rounded-xl text-sm font-bold text-muted-foreground hover:bg-muted transition-colors">
                      Gotowe
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-4">
                  {/* Ekran, z którego zgłaszasz (auto) */}
                  <div className="flex items-center gap-2 rounded-xl px-3 py-2 text-[11px]" style={{ background: `${ACCENT}0e`, border: `1px solid ${ACCENT}22` }}>
                    <MapPin className="w-3.5 h-3.5 shrink-0" style={{ color: ACCENT }} />
                    <span className="text-muted-foreground">Zgłaszasz z ekranu:</span>
                    <span className="font-bold" style={{ color: ACCENT }}>{screen}</span>
                  </div>

                  {/* Typ zgłoszenia */}
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Rodzaj</label>
                    <div className="mt-2 grid grid-cols-5 gap-2">
                      {TYPES.map(t => {
                        const active = type === t.value;
                        const Icon = t.icon;
                        return (
                          <button key={t.value} onClick={() => setType(t.value)}
                            className="flex flex-col items-center gap-1.5 py-2.5 rounded-xl transition-all border"
                            style={{
                              background: active ? t.bg : 'white',
                              borderColor: active ? t.color : '#e2e8f0',
                              boxShadow: active ? `0 4px 12px ${t.color}33` : 'none',
                            }}>
                            <Icon className="w-4 h-4" style={{ color: active ? t.color : '#64748b' }} />
                            <span className="text-[11px] font-bold" style={{ color: active ? t.color : '#475569' }}>
                              {t.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">{cfg.desc}</p>
                  </div>

                  {/* Jak bardzo przeszkadza (opcjonalne) — pomaga w triage */}
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                      Jak bardzo przeszkadza? <span className="normal-case font-normal">— opcjonalnie</span>
                    </label>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {URGENCY.map(u => {
                        const active = urgency === u.value;
                        const Icon = u.icon;
                        return (
                          <button key={u.value} type="button"
                            onClick={() => setUrgency(active ? null : u.value)}
                            className="flex flex-col items-center gap-1 py-2 rounded-xl transition-all border text-center"
                            style={{
                              background: active ? u.bg : 'white',
                              borderColor: active ? u.color : '#e2e8f0',
                              boxShadow: active ? `0 4px 12px ${u.color}33` : 'none',
                            }}>
                            <Icon className="w-4 h-4" style={{ color: active ? u.color : '#64748b' }} />
                            <span className="text-[11px] font-bold leading-none" style={{ color: active ? u.color : '#475569' }}>{u.label}</span>
                            <span className="text-[9px] leading-tight" style={{ color: active ? u.color : '#94a3b8' }}>{u.hint}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Tytuł */}
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Tytuł</label>
                    <input value={title} onChange={e => setTitle(e.target.value)} maxLength={200}
                      placeholder={type === 'BUG' ? 'np. Lista pojazdów nie ładuje się po kliknięciu filtru' : 'Krótkie podsumowanie'}
                      className="mt-1 w-full px-3 py-2 rounded-xl border border-border bg-muted/40 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:bg-card" />
                  </div>

                  {/* Podobne otwarte zgłoszenia — „Ja też" zamiast duplikatu */}
                  {similar.length > 0 && (
                    <div className="rounded-xl border border-sky-200 bg-sky-50/60 px-3 py-2.5">
                      <p className="text-[11px] font-bold text-sky-800 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />Może to samo już zgłoszono? Kliknij „Ja też", zamiast tworzyć duplikat.</p>
                      <div className="mt-2 flex flex-col gap-1.5">
                        {similar.map(t => {
                          const voted = t.hasVoted || votedIds.has(t.id);
                          return (
                            <div key={t.id} className="flex items-center gap-2 rounded-lg border border-sky-200 bg-card px-2.5 py-1.5">
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-[12px] font-semibold text-foreground">{t.title}</span>
                                <span className="block text-[10px] font-mono text-muted-foreground">{t.ticketNumber}</span>
                              </span>
                              <button type="button" onClick={() => voteFor(t)}
                                className={'inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-bold transition-colors ' + (voted ? 'bg-sky-600 text-white' : 'bg-sky-100 text-sky-700 hover:bg-sky-200')}>
                                <ThumbsUp className="w-3 h-3" />{voted ? 'Dołączono' : 'Ja też'}{t.voteCount > 0 ? ` · ${t.voteCount}` : ''}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Opis */}
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Opis</label>
                    <textarea value={description} onChange={e => setDescription(e.target.value)}
                      maxLength={10_000} rows={5}
                      placeholder={type === 'BUG' ? 'Co zrobiłeś, co zobaczyłeś, czego oczekiwałeś?' : 'Opisz szczegółowo — kontekst pomaga ustalić priorytet.'}
                      className="mt-1 w-full px-3 py-2 rounded-xl border border-border bg-muted/40 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:bg-card resize-none" />
                    <p className="text-[11px] text-muted-foreground mt-1 text-right">{description.length}/10000</p>
                  </div>

                  {/* Kroki do odtworzenia — tylko dla błędów (składane do context) */}
                  {type === 'BUG' && (
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground flex items-center gap-1.5">
                        <ListChecks className="w-3.5 h-3.5" />Kroki do odtworzenia <span className="normal-case font-normal">— opcjonalnie, bardzo pomaga</span>
                      </label>
                      <textarea value={steps} onChange={e => setSteps(e.target.value)} maxLength={2000} rows={3}
                        placeholder={'1. Wchodzę na…\n2. Klikam…\n3. Zamiast X widzę Y'}
                        className="mt-1 w-full px-3 py-2 rounded-xl border border-border bg-muted/40 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:bg-card resize-none" />
                    </div>
                  )}

                  {/* Szablon dla Modyfikacji — prowadzone pola (składane do context) */}
                  {type === 'MODYFIKACJA' && (
                    <div className="flex flex-col gap-3 rounded-xl border border-violet-200 bg-violet-50/50 px-3 py-3">
                      <p className="text-[11px] text-violet-700 font-semibold">Pomóż nam zrozumieć zmianę — te pola są opcjonalne, ale bardzo przyspieszają wycenę.</p>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Stan obecny</label>
                        <textarea value={currentState} onChange={e => setCurrentState(e.target.value)} maxLength={2000} rows={2}
                          placeholder="Jak działa to teraz?"
                          className="mt-1 w-full px-3 py-2 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Oczekiwana zmiana</label>
                        <textarea value={expectedChange} onChange={e => setExpectedChange(e.target.value)} maxLength={2000} rows={2}
                          placeholder="Jak powinno działać po zmianie?"
                          className="mt-1 w-full px-3 py-2 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Uzasadnienie</label>
                        <textarea value={rationale} onChange={e => setRationale(e.target.value)} maxLength={2000} rows={2}
                          placeholder="Po co ta zmiana? Co daje?"
                          className="mt-1 w-full px-3 py-2 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
                      </div>
                    </div>
                  )}

                  {/* Czego dotyczy? — podłączenie do konkretnego obiektu (opcjonalne) */}
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                      Czego dotyczy? <span className="normal-case font-normal">— opcjonalnie (formularz, pojazd, kierowca, linia)</span>
                    </label>
                    <div className="mt-2"><EntityLinkPicker value={linked} onChange={setLinked} /></div>
                  </div>

                  {/* Załączniki (opcjonalne) */}
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground flex items-center gap-1.5">
                      <Paperclip className="w-3.5 h-3.5" />Załączniki <span className="normal-case font-normal">— opcjonalnie (zrzuty ekranu, zdjęcia, dokumenty)</span>
                    </label>
                    {files.length > 0 && (
                      <div className="mt-2 flex flex-col gap-1.5">
                        {files.map((f, i) => (
                          <div key={i} className="flex items-center gap-2 rounded-xl border border-border bg-muted/40 px-3 py-1.5">
                            <Paperclip className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                            <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-foreground">{f.name}</span>
                            <span className="shrink-0 text-[11px] text-muted-foreground">{fileSize(f.size)}</span>
                            <button type="button" onClick={() => removeFile(i)}
                              className="shrink-0 text-muted-foreground hover:text-rose-600" title="Usuń">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {files.length < MAX_FILES && (
                      <label className="mt-2 flex cursor-pointer items-center justify-center gap-1.5 rounded-xl border border-dashed border-border bg-muted/30 px-3 py-2 text-[12px] font-semibold text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground">
                        <Paperclip className="w-3.5 h-3.5" />
                        Dołącz plik{files.length > 0 ? ' (kolejny)' : 'i'}
                        {/* B-191: bez tej podpowiedzi wklejanie zostałoby funkcją,
                            o której wie tylko ten, kto ją zbudował. */}
                        <span className="normal-case font-normal text-muted-foreground">— albo wklej zrzut (Ctrl+V)</span>
                        <input type="file" multiple className="hidden"
                          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                          onChange={e => { addFiles(e.target.files); e.currentTarget.value = ''; }} />
                      </label>
                    )}
                  </div>

                  {/* Kontekst techniczny */}
                  <details className="rounded-xl border border-border bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground">
                    <summary className="cursor-pointer font-semibold text-foreground flex items-center gap-2">
                      <AlertCircle className="w-3.5 h-3.5" />
                      Dane techniczne dołączone do zgłoszenia
                    </summary>
                    <ul className="mt-2 space-y-0.5 leading-relaxed font-mono">
                      <li>Strona: {baseContext.url ?? '—'}</li>
                      <li>Przeglądarka: {baseContext.userAgent?.split(') ')[1] ?? '—'}</li>
                      <li>Rozdzielczość: {baseContext.viewport ?? '—'}</li>
                      <li>Rola: {user?.role ?? '—'}</li>
                    </ul>
                  </details>
                </div>
              )}

              {!submitted && (
                <div className="flex-shrink-0 px-5 py-3 border-t border-border flex items-center justify-end gap-2">
                  <button onClick={() => setOpen(false)} disabled={submitting}
                    className="px-3 py-2 rounded-xl text-[12px] font-bold text-muted-foreground hover:bg-muted transition-colors">
                    Anuluj
                  </button>
                  <button onClick={submit}
                    disabled={submitting || title.trim().length < 3 || description.trim().length < 3}
                    className="px-4 py-2 rounded-xl text-[12px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-40 transition-all bg-primary hover:bg-primary/90">
                    {submitting
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Wysyłanie…</>
                      : <><Send className="w-3.5 h-3.5" />Wyślij</>}
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
